<template>
  <div class="flex flex-col min-h-screen bg-gray-100">
    <main class="flex-grow flex flex-col items-center justify-center p-6">
      <LogoBig />

      <div
        v-if="currentStep === 1"
        class="items-center justify-center text-center"
      >
        <div
          v-if="pageState === 'OPEN' || pageState === 'CLOSED'"
          class="flex items-center w-full sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto mb-4"
        >
          <hr class="flex-grow border-gray-300" />
          <h2
            class="text-xl font-semibold text-gray-700 mx-4 whitespace-nowrap"
          >
            {{ getWelcomeFirstTitle() }}
          </h2>
          <hr class="flex-grow border-gray-300" />
        </div>

        <h1
          v-if="pageState === 'OPEN' || pageState === 'CLOSED'"
          class="text-4xl font-bold text-gray-800 mb-4 max-w-3xl"
        >
          {{ welcomeTitle }}
        </h1>
        <div v-if="pageState === 'OPEN'">
          <p
            v-if="welcomeSubtitle"
            class="text-lg text-gray-600 mb-6 text-center max-w-3xl"
          >
            {{ welcomeSubtitle }}
          </p>
        </div>
        <div v-else-if="pageState === 'CLOSED'">
          <img
            src="@/assets/completed.png"
            alt="Completada"
            class="mx-auto mb-6 w-[72px] h-[72px]"
          />
          <p
            class="text-lg font-semibold text-gray-600 mb-2 text-center max-w-2xl"
          >
            {{ $t('welcome-subtitle-evaluation-completed') }}
          </p>
          <p class="text-lg text-gray-600 mb-6 text-center max-w-2xl">
            {{
              $t(
                type === 'matchmind'
                  ? 'welcome-subtitle-evaluation-completed-results-sent-matchmind'
                  : 'welcome-subtitle-evaluation-completed-results-sent'
              )
            }}
          </p>
          <p class="text-md text-gray-400 mb-6 text-center max-w-2xl">
            {{ $t('welcome-subtitle-evaluation-completed-close-window') }}
          </p>
        </div>
        <div v-else-if="pageState === 'NOT_AVAILABLE'">
          <img
            src="@/assets/not-available.png"
            alt="No disponible"
            class="mx-auto mb-6 w-[72px] h-[72px]"
          />

          <p
            class="text-2xl font-semibold text-gray-700 mb-6 text-center max-w-2xl"
          >
            {{ $t('welcome-title-not-available') }}
          </p>
          <p class="text-lg text-gray-600 mb-6 text-center max-w-2xl">
            {{
              type === 'matchmind'
                ? $t('welcome-subtitle-not-available-matchmind')
                : $t('welcome-subtitle-not-available')
            }}
          </p>
        </div>
      </div>
      <!-- Step 1: Terms Acceptance -->
      <div
        v-if="currentStep === 1 && pageState === 'OPEN'"
        class="flex items-center mb-6 text-center"
      >
        <input
          id="accept-terms"
          type="checkbox"
          v-model="isAcceptedTerms"
          class="mr-2 w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
        />
        <label for="accept-terms" class="text-gray-600">
          {{ $t('terms-acceptance') }}
          <a
            :href="termsAndConditionsUrl"
            target="_blank"
            class="cursor-pointer font-bold hover:underline"
          >
            {{ $t('terms-and-conditions') }}
          </a>
          {{ $t('and') }}
          <a
            :href="privacyPolicyUrl"
            target="_blank"
            class="cursor-pointer font-bold hover:underline"
          >
            {{ $t('privacy-policy') }} </a
          >.
          <span class="text-red-500">*</span>
        </label>
      </div>

      <!-- Step 2: Additional Confirmation / Test-Specific Instructions -->
      <div v-if="currentStep === 2" class="items-center justify-center text-center">
        <!-- Configurable test-specific informed consent with instructions -->
        <template v-if="consentConfig.mode === 'test'">
          <div class="text-gray-600 mb-6 max-w-3xl mx-auto text-center">
            <h2 class="font-bold text-xl mb-4">
              {{ $t(consentConfig.titleKey) }}
            </h2>
            <p class="mb-4" v-html="$t(consentConfig.bodyKey)">
            </p>
          </div>

          <div class="flex items-center justify-center mb-6 text-center max-w-3xl mx-auto">
            <input
              id="accept-additional"
              type="checkbox"
              v-model="isAcceptedAdditional"
              class="mr-2 w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <label for="accept-additional" class="text-gray-600">
              {{ $t(consentConfig.confirmationKey) }}
              <a
                :href="termsUrlWithParams"
                target="_blank"
                rel="noopener noreferrer"
                class="cursor-pointer font-bold hover:underline ml-1"
              >
                {{ $t('terms-and-conditions') }}
              </a>
              <span class="text-red-500 ml-1">*</span>
            </label>
          </div>
        </template>

        <!-- Default consent content for other types -->
        <template v-else>
          <div class="text-gray-600 mb-6 max-w-3xl mx-auto text-center">
            <h2 class="font-bold text-xl mb-2">
              {{
                $t(
                  type === 'matchmind'
                    ? 'consent-title-matchmind'
                    : 'consent-title'
                )
              }}
            </h2>
            <p
              class="mb-1"
              v-html="
                $t(
                  type === 'matchmind'
                    ? 'consent-intro-matchmind'
                    : 'consent-intro'
                )
              "
            ></p>
            <p
              class="mb-1"
              v-html="
                $t(
                  type === 'matchmind'
                    ? 'consent-summary-matchmind'
                    : 'consent-summary'
                )
              "
            ></p>
            <p class="mb-1">
              <strong>{{ $t('important') }}</strong>
            </p>
            <ul class="list-disc ml-6 text-left">
              <li
                v-for="(item, index) in importantList"
                :key="index"
                v-html="item"
              ></li>
            </ul>
            <p
              class="mb-1"
              v-html="
                $t(
                  type === 'matchmind'
                    ? 'consent-use-matchmind'
                    : 'consent-use'
                )
              "
            ></p>
            <p
              class="mb-1"
              v-html="
                $t(
                  type === 'matchmind'
                    ? 'consent-voluntary-matchmind'
                    : 'consent-voluntary'
                )
              "
            ></p>
            <p
              class="mb-1"
              v-html="
                $t(
                  type === 'matchmind'
                    ? 'consent-honesty-matchmind'
                    : 'consent-honesty'
                )
              "
            ></p>
          </div>

          <div class="flex items-center justify-center mb-6 text-center">
            <input
              id="accept-additional"
              type="checkbox"
              v-model="isAcceptedAdditional"
              class="mr-2 w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <label for="accept-additional" class="text-gray-600">
              {{ $t('consent-confirmation') }}
              <span class="text-red-500">*</span>
            </label>
          </div>
        </template>
      </div>

      <!-- Navigation Button -->
      <button
        v-if="pageState === 'OPEN'"
        @click="handleButtonClick"
        :disabled="currentStep === 1 ? !isAcceptedTerms : !isAcceptedAdditional"
        class="bg-primary hover:bg-secondary text-black hover:text-white font-bold py-3 px-6 rounded-lg transition duration-300 disabled:bg-gray-300 disabled:text-gray-500"
      >
        {{ currentStep === 1 ? $t('next') : $t('start-evaluation') }}
      </button>
    </main>
    <MedeaFooter />
  </div>
</template>

<script>
import LogoBig from '@/components/LogoBig.vue';
import MedeaFooter from './MedeaFooter.vue';
import { clients } from '@/config/clients';
import { useI18n } from 'vue-i18n';
import { useGlobalStore } from '@/stores/global';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

export default {
  name: 'LandingPage',
  components: {
    LogoBig,
    MedeaFooter,
  },
  setup() {
    const store = useGlobalStore();
    const { t, tm, locale } = useI18n();
    const route = useRoute();

    const theme = document.documentElement.getAttribute('data-theme');
    const pageState = computed(() => store.state);
    const type = computed(
      () => window.location.search.match(/type=([^&]+)/)?.[1]
    );

    const consentConfig = computed(() => {
      const currentType = type.value;

      // Map of test types to their i18n keys and config
      const testConfigs = {
        phq9: {
          mode: 'test',
          titleKey: 'phq9-instructions-title',
          bodyKey: 'phq9-instructions-body',
          confirmationKey: 'phq9-consent-confirmation',
          termsHref: '/terms',
        },
        gad7: {
          mode: 'test',
          titleKey: 'gad7-instructions-title',
          bodyKey: 'gad7-instructions-body',
          confirmationKey: 'gad7-consent-confirmation',
          termsHref: '/terms',
        },
        // To add a new test, just add a new entry here
        // newTestType: {
        //   mode: 'test',
        //   titleKey: 'newTest-instructions-title',
        //   bodyKey: 'newTest-instructions-body',
        //   confirmationKey: 'newTest-consent-confirmation',
        //   termsHref: '/terms',
        // },
      };

      if (currentType && testConfigs[currentType]) {
        return testConfigs[currentType];
      }

      // Fallback to default Medea Mix / MatchMind consent
      return { mode: 'default' };
    });

    const importantList = computed(() => {
      if (type.value === 'matchmind') {
        return Object.values(tm('important-list-matchmind'));
      }
      return Object.values(tm('important-list'));
    });

    const termsAndConditionsUrl = computed(() => {
      const theme = document.documentElement.getAttribute('data-theme');

      const client = clients[theme];
      const terms = client.termsAndConditions;

      return typeof terms === 'string'
        ? terms
        : terms[locale.value] || terms['en'];
    });

    const privacyPolicyUrl = computed(() => {
      const theme = document.documentElement.getAttribute('data-theme');

      const client = clients[theme];
      const policy = client.privacyPolicy;

      return typeof policy === 'string'
        ? policy
        : policy[locale.value] || policy['en'];
    });

    const welcomeTitle = computed(() => {
      const title = window.location.search.match(/title=([^&]+)/)?.[1];
      if (title) {
        return decodeURIComponent(title);
      }
      const theme = document.documentElement.getAttribute('data-theme');
      return t(
        type.value === 'matchmind' ? 'welcome-text-matchmind' : 'welcome-text',
        {
          clientName: clients[theme]?.clientName,
        }
      );
    });

    const welcomeSubtitle = computed(() => {
      const store = useGlobalStore();
      // Use landingConfig welcome_text if available and not empty
      if (
        store.landingConfig?.welcome_text &&
        store.landingConfig.welcome_text.trim() !== ''
      ) {
        return store.landingConfig.welcome_text;
      }

      // Fall back to translated welcome-subtitle if no config text
      if (type.value === 'matchmind') {
        return t('welcome-subtitle-matchmind');
      } else if (type.value === 'phq9') {
        return t('welcome-subtitle-phq9');
      } else if (type.value === 'gad7') {
        return t('welcome-subtitle-gad7');
      }
      return t('welcome-subtitle');
    });

    const termsUrlWithParams = computed(() => {
      const basePath = consentConfig.value.termsHref || '/terms';
      const queryParams = new URLSearchParams(route.query).toString();
      return queryParams ? `${basePath}?${queryParams}` : basePath;
    });

    return {
      pageState,
      importantList,
      type,
      consentConfig,
      termsAndConditionsUrl,
      privacyPolicyUrl,
      welcomeTitle,
      welcomeSubtitle,
      theme,
      termsUrlWithParams,
    };
  },
  data() {
    return {
      currentStep: 1,
      isAcceptedTerms: false,
      isAcceptedAdditional: false,
    };
  },
  methods: {
    getWelcomeFirstTitle() {
      const theme = document.documentElement.getAttribute('data-theme');
      const type = this.$route.query.type;

      if (theme === 'PSYPILOT' && type === 'matchmind') {
        return 'Match Mind©';
      } else if (theme === 'PSYPILOT' && type === 'live') {
        return this.$t('welcome-title-live');
      } else if (theme === 'PSYPILOT' && type === 'phq9') {
        return 'PHQ-9';
      } else if (theme === 'PSYPILOT' && type === 'gad7') {
        return 'GAD-7';
      } else if (theme === 'PSYPILOT') {
        return 'Medea Mix©';
      } else {
        return this.$t('welcome-title');
      }
    },
    handleButtonClick() {
      if (this.currentStep === 1) {
        this.currentStep = 2;
      } else {
        this.$router.push({ path: '/mix', query: this.$route.query });
      }
    },
  },
};
</script>
